import {
  createMailroomMapping,
  RegisteredCompany,
  useRegisteredCompanies,
} from '@commenda/api';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { InfoIcon } from 'lucide-react';
import { useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';

export const AddExistingDRACompanyModal = ({
  isOpen,
  onClose,
  corporationId,
}: {
  isOpen: boolean;
  onClose: () => void;
  corporationId: number;
}) => {
  const { data: registeredCompanies } = useRegisteredCompanies();
  const [selectedCompany, setSelectedCompany] =
    useState<RegisteredCompany | null>(null);
  const { setNotification } = useNotification();

  const registeredCompaniesOptions = registeredCompanies?.map((company) => ({
    label: company.name,
    value: company.id,
  }));

  const handleSubmit = async () => {
    const res = await createMailroomMapping({
      corporationId,
      sourceId: selectedCompany?.id,
    });
    if (!res.success) {
      setNotification({
        message: 'Failed to map DRA Company',
        show: true,
        title: 'Error',
        valid: false,
      });
      return;
    }

    setNotification({
      message: 'DRA Company mapped successfully',
      show: true,
      title: 'Success',
      valid: true,
    });

    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent preventCloseOnOutsideClick>
        <DialogHeader>
          <DialogTitle>Add existing DRA Company</DialogTitle>
        </DialogHeader>
        <div className="p-3xl max-h-[540px] overflow-y-auto">
          <div className="flex flex-col gap-2xl">
            <Alert
              variant="destructive"
              title="This will map an existing DRA Company to this corporation"
              description="This action cannot be undone. Contact support if you need to change this."
              icon={<InfoIcon className="h-4 w-4 text-text-error mt-1" />}
            />
            <Select
              onValueChange={(value: string) => {
                setSelectedCompany(
                  registeredCompanies?.find((company) => company.id === value)
                );
              }}
              value={selectedCompany?.id}
            >
              <SelectTrigger id="existing-dra-company">
                <SelectValue placeholder="Select DRA Company" />
              </SelectTrigger>
              <SelectContent>
                {registeredCompaniesOptions?.map((company) => (
                  <SelectItem key={company.value} value={company.value}>
                    {company.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!selectedCompany}
            onClick={handleSubmit}
          >
            <span>Confirm</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
